import { MoreHoriz } from '@mui/icons-material'
import { IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { find, isEmpty, map } from 'lodash'
import { useCallback, useContext, useMemo, useState } from 'react'
import { deleteExperiment, ExperimentListItem } from '../../api'
import { YEAR_BOUNDARY } from '../../constants/experimentYearBoundary'
import { PERMISSIONS } from '../../constants/permissions'
import { useOpenExperiment } from '../../hooks/useOpenExperiment'
import { AppStateContext } from '../../state/AppProvider'
import dayjs from '../../utils/date'
import { hasPermission } from '../../utils/permissions'
import { openTeamsShareDialog } from '../../utils/teamsShare'
import CustomDialog from '../dialogs/CustomDialog/CustomDialog'
import styles from './ExperimentsTable.module.css'
import { ExperimentsTableHead } from './ExperimentsTableHead'
import { HistoryModal } from './HistoryModal'
import { RowActionsMenu } from './RowActionsMenu'

interface ExperimentsTableProps {
  headTitles: string[]
  order: 'asc' | 'desc'
  orderBy: string
  data: ExperimentListItem[]
  year?: number
  onRequestSort: (property: string) => void
  refreshData: () => void
}

const getExperimentCodeCell = (code?: string | null, status?: string | null) => {
  if (status === 'Draft') {
    return <div className={styles.codeWrapperYellow}>{code}</div>
  } else if (status === 'Submit') {
    return <div className={styles.codeWrapperBlue}>{code}</div>
  } else if (status === 'Pending Resubmission') {
    return <div className={styles.codeWrapperRed}>{code}</div>
  } else {
    return <div>{code}</div>
  }
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return ''
  return dayjs.utc(dateString).format('D MMM YYYY')
}

export const ExperimentsTable = ({
  headTitles,
  order,
  orderBy,
  data,
  year,
  onRequestSort,
  refreshData,
}: ExperimentsTableProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const isRowActionsMenuOpen = Boolean(anchorEl)
  const appStateContext = useContext(AppStateContext)
  const [shouldShowAccessFiles, setShouldShowAccessFiles] = useState(false)
  const { openExperiment } = useOpenExperiment()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const experimentData = map(data, item => {
    return {
      ...item.data,
      id: item.id,
    }
  })
  const isEditDisabled = !hasPermission(PERMISSIONS.editExperiment)
  const isDeleteDisabled = !hasPermission(PERMISSIONS.deleteExperiment)
  const isBeforeYearBoundary = Boolean(year && year <= YEAR_BOUNDARY)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)

  const selectedExperiment = useMemo(
    () => find(experimentData, item => item.id === selectedRowId),
    [experimentData, selectedRowId],
  )

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    const experiment = find(experimentData, item => item.id === rowId)
    setShouldShowAccessFiles(!isEmpty(experiment?.extra_data.folder_path?.field_value))
    setAnchorEl(event.currentTarget)
    setSelectedRowId(rowId)
  }

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
    setSelectedRowId(null)
  }, [])

  const onViewDetails = useCallback(() => {
    openExperiment(selectedRowId ?? '', true).catch(err => {
      console.error(`Error fetching chat history for experiment ${selectedRowId}`, err)
    })
  }, [selectedRowId, openExperiment])

  const onEditExperiment = useCallback(() => {
    openExperiment(selectedRowId ?? '', false).catch(err => {
      console.error(`Error fetching chat history for experiment ${selectedRowId}`, err)
    })
  }, [selectedRowId, openExperiment])

  const onAccessFiles = useCallback(() => {
    const folderPath = selectedExperiment?.extra_data.folder_path?.field_value ?? ''
    window.open(folderPath, '_blank')
  }, [selectedExperiment])

  const onDeleteExperiment = useCallback(() => {
    deleteExperiment(selectedRowId ?? '')
      .then(response => {
        setIsDeleteModalOpen(false)

        if (response.ok) {
          refreshData()
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: {
              message: 'Experiment deleted successfully.',
              severity: 'success',
            },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: {
              message: 'Delete failed. Try again later.',
              severity: 'warning',
            },
          })
        }
      })
      .catch(err => {
        setIsDeleteModalOpen(false)
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: {
            message: 'Delete failed. Try again later.',
            severity: 'warning',
          },
        })
        console.error(`Error deleting experiment ${selectedRowId}`, err)
      })
  }, [selectedRowId, refreshData, appStateContext])

  const onShareToTeams = useCallback(() => {
    const experimentCode = selectedExperiment?.planning_basics.experiment_code.field_value ?? ''
    const experimentUrl = `${window.location.origin}/#/app/experiments/${encodeURIComponent(experimentCode)}`
    const message = `Check out experiment ${experimentCode}`
    openTeamsShareDialog(message, experimentUrl)
    handleCloseMenu()
  }, [selectedRowId, selectedExperiment, handleCloseMenu])

  return (
    <>
      <TableContainer className={styles.tableContainer}>
        <Table>
          <ExperimentsTableHead
            headTitles={headTitles}
            onRequestSort={onRequestSort}
            order={order}
            orderBy={orderBy}
          />
          <TableBody className={styles.tableBody}>
            {map(experimentData, (item, index) => (
              <TableRow key={index}>
                <TableCell
                  key={`${index}-experiment-code`}
                  className={styles.tableCell}>
                  {getExperimentCodeCell(
                    item.planning_basics.experiment_code.field_value,
                    item.planning_basics.experiment_status.field_value,
                  )}
                </TableCell>
                <TableCell
                  key={`${index}-country`}
                  className={styles.tableCell}>
                  {item.planning_basics.country.field_value}
                </TableCell>
                {isBeforeYearBoundary ? (
                  <TableCell
                    key={`${index}-gla-strategic-priority`}
                    className={styles.tableCell}>
                    {item.planning_basics.application_area?.field_value}
                  </TableCell>
                ) : (
                  <TableCell
                    key={`${index}-gla-strategic-priority`}
                    className={styles.tableCell}>
                    {item.planning_basics.mto_initiative?.field_value}
                  </TableCell>
                )}
                {!isBeforeYearBoundary && year && (
                  <TableCell
                    key={`${index}-global-charter`}
                    className={styles.tableCell}>
                    {item.planning_basics.global_charter?.field_value}
                  </TableCell>
                )}
                <TableCell
                  key={`${index}-methodology`}
                  className={styles.tableCell}>
                  {item.planning_basics.methodology.field_value}
                </TableCell>
                <TableCell
                  key={`${index}-experiment-start-date`}
                  className={styles.tableCell}>
                  {formatDate(item.setup_details.experiment_start_date.field_value)}
                </TableCell>
                <TableCell
                  key={`${index}-experiment-phase-high-level`}
                  className={styles.tableCell}>
                  {item.planning_basics.experiment_phase_high_level?.field_value}
                </TableCell>
                {isBeforeYearBoundary && (
                  <>
                    <TableCell
                      key={`${index}-brand`}
                      className={styles.tableCell}>
                      {item.planning_basics.brand?.field_value}
                    </TableCell>
                    <TableCell
                      key={`${index}-high-value`}
                      className={styles.tableCell}>
                      {item.extra_data.high_value?.field_value}
                    </TableCell>
                  </>
                )}

                <TableCell
                  key={`${index}-row-actions`}
                  className={styles.tableCell}>
                  <IconButton
                    onClick={e => handleClick(e, item.id)}
                    className={styles.rowActionsButton}>
                    <MoreHoriz />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <RowActionsMenu
          id="row-actions-menu"
          anchorEl={anchorEl}
          selectedRowId={selectedRowId}
          open={isRowActionsMenuOpen}
          handleClose={handleCloseMenu}
          onEditExperiment={onEditExperiment}
          onShareToTeams={onShareToTeams}
          onAccessFiles={onAccessFiles}
          onDeleteExperiment={() => {
            setIsDeleteModalOpen(true)
            setAnchorEl(null)
          }}
          shouldShowAccessFiles={shouldShowAccessFiles}
          isDeleteDisabled={isDeleteDisabled}
          isEditDisabled={isEditDisabled}
          onViewDetails={onViewDetails}
          isBeforeYearBoundary={isBeforeYearBoundary}
          onViewHistory={() => setIsHistoryModalOpen(true)}
        />
      </TableContainer>
      <CustomDialog
        open={isDeleteModalOpen}
        setClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedRowId(null)
        }}
        title="Delete this experiment permanently?"
        primaryAction={() => {
          onDeleteExperiment()
          setSelectedRowId(null)
        }}
        primaryActionButtonText="Delete"
        secondaryAction={() => {
          setIsDeleteModalOpen(false)
          setSelectedRowId(null)
        }}
        secondaryActionButtonText="Cancel">
        <Stack>
          <Typography className={styles.deleteDialogContentText}>
            This action cannot be undone. If there's an active conversation linked to this experiment, it will stay
            available for reference.
          </Typography>
        </Stack>
      </CustomDialog>
      <HistoryModal
        open={isHistoryModalOpen}
        setClose={() => setIsHistoryModalOpen(false)}
        experimentId={selectedRowId ?? ''}
      />
    </>
  )
}
